import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { ArenaLogo } from "@/components/common/ArenaLogo";
import LogoBg from "../../assets/logo-bg.png";
import Logo from "../../assets/logo.png";
import help from "../../assets/help.svg";
import stepOneImage from "../../assets/signup-step-one.svg";
import language from "../../assets/language.svg";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useSignupPreviewOptional } from "@/contexts/SignupPreviewContext";
import { usePortalBranding } from "@/contexts/portalBranding";
import { CheckoutPreview } from "@/components/CheckoutPreview/CheckoutPreview";
import { CommissionTable } from "@/components/CommissionTable/CommissionTable";
// import { useBrandAuth } from "@/contexts/BrandAuthContext/context";
import { Navigate } from "react-router-dom";

interface AuthLayoutProps {
  navState?: "no-hide" | "small-screen-hide" | "hide";
}

export function AuthLayout({ navState = "no-hide" }: AuthLayoutProps) {
  // const { user } = useBrandAuth();
  const location = useLocation();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Check if we're on the brand signup page
  const isBrandSignupPage = location.pathname === "/brand/auth/signup";

  // Use optional context hook (returns undefined if not available)
  const signupContext = useSignupPreviewOptional();
  const portalBranding = usePortalBranding();

  // Use context values if available, otherwise use defaults
  const signupStep = signupContext?.signupStep ?? 1;
  const formData = signupContext?.formData ?? {
    companyName: "",
    niches: [] as Array<{ id: string; name: string }>,
  };

  // Send form data updates to iframe in real-time
  useEffect(() => {
    if (
      isBrandSignupPage &&
      signupStep === 2 &&
      iframeRef.current?.contentWindow
    ) {
      console.log("Sending data to iframe:", formData);
      // Send updated form data to iframe
      iframeRef.current.contentWindow.postMessage(
        {
          type: "SIGNUP_FORM_DATA_UPDATE",
          formData: formData,
        },
        "*"
      );
    }
  }, [isBrandSignupPage, signupStep, formData]);

  // Listen for data requests from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data &&
        event.data.type === "REQUEST_INITIAL_DATA" &&
        iframeRef.current?.contentWindow
      ) {
        console.log("Iframe requested initial data, sending:", formData);
        iframeRef.current.contentWindow.postMessage(
          {
            type: "SIGNUP_FORM_DATA_UPDATE",
            formData: formData,
          },
          "*"
        );
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [formData]);

  // If user is already authenticated and trying to access auth pages, redirect to dashboard
  // if (user) {
  //   // Don't redirect if they're just installing the app
  //   if (location.pathname !== "/brand/auth") {
  //     return <Navigate to="/brand/dashboard" replace />;
  //   }
  // }
  //For signup, check if there's a valid token (temp or permanent)
  if (location.pathname === "/brand/auth/signup") {
    const tempToken = localStorage.getItem("brand_temp_token");
    const permanentToken = localStorage.getItem("brand_token");
    if (!tempToken && !permanentToken) {
      return <Navigate to="/brand/auth" replace />;
    }
  }
  return (
    <div className="relative flex p-0 m-0 h-screen overflow-hidden">
      {/* Backdrop: the pattern sits on its own layer so dark mode can invert
          just the artwork (dark strokes -> light) without touching the
          gradient washes above it. */}
      <div className="absolute inset-0 -z-50 w-full">
        <div
          className="absolute inset-0 bg-cover lg:bg-[size:95%_auto] bg-no-repeat bg-[image:var(--logo-bg-image)] dark:invert dark:opacity-40"
          style={{
            ["--logo-bg-image" as string]: `url(${LogoBg})`,
          }}
        ></div>
        <div className="absolute inset-0 bg-[linear-gradient(50deg,hsl(var(--background)/0),hsl(var(--background)/1)),radial-gradient(171.56%_195.04%_at_20.94%_47.24%,hsl(var(--background)/0)_0%,hsl(var(--background)/1)_41.2%)]"></div>
      </div>

      <div
        className={`fixed z-[50] bottom-[20px] w-[95%] left-[9px] md:top-[18px] md:pt-2 pl-2 md:pl-0 h-[50px] md:left-[20px] md:h-[95%] md:w-[50px] bg-card rounded-[7px] border flex md:flex-col items-center justify-between ${
          navState === "hide"
            ? "!hidden"
            : navState === "small-screen-hide"
            ? "hidden md:flex"
            : ""
        }`}
      >
        <ArenaLogo className="w-10 border border-black rounded-[12px] bg-[#f6f6f6]" />
        <div className="flex md:flex-col items-center gap-5 justify-center md:last:pb-4 pr-4 md:pr-0">
          <img src={help} className="w-[16px] dark:invert" />
          <ThemeToggle className="h-7 w-7" />
          <img src={language} className="w-[16px] dark:invert" />
        </div>
      </div>

      <div className="hidden lg:flex lg:w-3/5 bg-cover bg-center">
        {isBrandSignupPage ? (
          <div className="m-auto pl-8 flex items-center justify-center w-full h-full">
            {signupStep === 1 && (
              <img
                src={stepOneImage}
                alt="Signup Step 1"
                className="max-w-[80%] max-h-[80%] object-contain"
              />
            )}
            {signupStep === 2 && (
              <div className="flex items-center justify-center p-8 relative w-full h-full">
                {/* iPhone Frame */}
                <div className="relative z-10">
                  {/* Phone Frame - 3D Effect */}
                  <div
                    className="relative"
                    style={{
                      width: "342px",
                      height: "667px",
                      perspective: "1200px",
                    }}
                  >
                    <div
                      className="absolute inset-0 rounded-[3rem] shadow-[0_20px_60px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.1)_inset]"
                      style={{
                        background:
                          "linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)",
                        padding: "10px",
                        transformStyle: "preserve-3d",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      {/* Phone Bezel with Shine */}
                      <div
                        className="w-full h-full rounded-[2.5rem] overflow-hidden relative"
                        style={{
                          background: "#000",
                          boxShadow:
                            "inset 0 0 30px rgba(0,0,0,0.8), inset 0 2px 4px rgba(255,255,255,0.1), 0 0 50px rgba(0,0,0,0.5)",
                        }}
                      >
                        {/* Notch */}
                        <div
                          className="absolute top-0 left-1/2 transform -translate-x-1/2 z-20"
                          style={{
                            width: "150px",
                            height: "30px",
                            background: "#000",
                            borderBottomLeftRadius: "20px",
                            borderBottomRightRadius: "20px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                          }}
                        />

                        {/* Screen Content - Iframe */}
                        <iframe
                          ref={iframeRef}
                          src="/brand/simplified-preview"
                          className="w-full h-full border-0 bg-card"
                          style={{
                            width: "330px",
                            height: "100%",
                            border: "none",
                            display: "block",
                            overflowY: "auto",
                          }}
                          title="Brand Preview"
                          onLoad={() => {
                            // Send initial data when iframe loads
                            console.log(
                              "Iframe loaded, sending initial data:",
                              formData
                            );
                            if (iframeRef.current?.contentWindow) {
                              // Small delay to ensure iframe is ready
                              setTimeout(() => {
                                iframeRef.current?.contentWindow?.postMessage(
                                  {
                                    type: "SIGNUP_FORM_DATA_UPDATE",
                                    formData: formData,
                                  },
                                  "*"
                                );
                              }, 100);
                            }
                          }}
                        />

                        {/* Home Indicator */}
                        <div
                          className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-20"
                          style={{
                            width: "134px",
                            height: "5px",
                            background: "rgba(255,255,255,0.3)",
                            borderRadius: "3px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                          }}
                        />
                      </div>

                      {/* Side Shine Effect */}
                      <div
                        className="absolute inset-0 rounded-[3rem] pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)",
                          mixBlendMode: "overlay",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {signupStep === 3 && (
              <div className="flex flex-col items-center justify-center gap-1 w-full h-full">
                {/* Left side: Image */}
                <div className="flex items-end justify-center w-[90%]">
                  {/* <img
                    src={stepThreeImage}
                    alt="Signup Step 3"
                    className=" w-1/2 max-h-full object-contain"
                  /> */}
                  <div className=" flex items-center w-1/2 justify-center min-h-0">
                    <CheckoutPreview
                      discountPercentage={
                        signupContext?.formData?.programSettings
                          ?.discountPercentage || "10"
                      }
                    />
                  </div>
                </div>
                {/* Right side: Preview components */}
                <div
                  className="flex flex-col
                 items-center justify-center h-min gap-1"
                >
                  <div className="flex items-center justify-center min-h-0">
                    <CommissionTable
                      discountPercentage={
                        signupContext?.formData?.programSettings
                          ?.discountPercentage || "10"
                      }
                      commissionRate={
                        signupContext?.formData?.programSettings
                          ?.commissionRate || "10"
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="m-auto">
            {portalBranding.isWhiteLabel ? (
              portalBranding.portalLogo ? (
                <img
                  src={portalBranding.portalLogo}
                  alt={portalBranding.portalName}
                  className="max-w-[247px] max-h-[247px] object-contain"
                />
              ) : null
            ) : portalBranding.isReady ? (
              <img
                src={Logo}
                alt="logo"
                className="w-[247px] py-4 p-0 m-0 bg-arena-logo-gradient border-[5px] border-black backdrop-blur-[4px] rounded-[73px] dark:bg-[#f6f6f6]"
              />
            ) : null}
          </div>
        )}
      </div>
      <Outlet />
    </div>
  );
}
